import React, { Component, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  silent?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

const SILENT_PATTERNS = [
  '_leaflet_pos',
  'Invalid hook call',
  'findDOMNode',
  'Context Lost',
  'WebGLRenderer',
];

function isSilentError(error: Error | undefined): boolean {
  if (!error?.message) return false;
  return SILENT_PATTERNS.some(p => error.message.includes(p));
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isSilentError(error)) {
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isSilentError(error)) return;
    // Use originalConsole to bypass the error overlay suppression in main.tsx
    const log = (window as any).__originalConsoleError ?? console.error;
    log("Caught error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="p-6 border border-red-500/40 rounded-lg bg-red-950/40 text-red-200 space-y-4">
          <h2 className="text-lg font-bold">Something went wrong</h2>
          <p className="text-sm text-red-300">
            An error occurred in this section. You can try again without reloading the whole page.
          </p>
          {this.state.error && (
            <div className="p-3 bg-black/40 rounded border border-red-800 overflow-auto">
              <pre className="text-xs whitespace-pre-wrap text-red-400">
                {this.state.error.message}
              </pre>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-sm font-medium transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
